import { useState } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import { TAUX_TVA, TAUX_REMUNERATION, SEUIL_MINIMUM } from '../../utils/calculs'
import { tarifAccueilPour } from '../../utils/tarifsJournee'

const CATEGORIES_SPA = ['massages', 'facials']
const CATEGORIE_HYDRO = 'hydrotherapy'

function parseDate(str) {
  // Format JJ.MM.AAAA
  const [j, m, a] = str.trim().split('.')
  if (!j || !m || !a) return null
  return `${a}-${m.padStart(2,'0')}-${j.padStart(2,'0')}`
}

function parseMontant(str) {
  if (!str) return 0
  return parseFloat(str.replace(/[€\s]/g, '').replace(',', '.')) || 0
}

function normaliserNom(str) {
  return (str || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

// Majuscules sans accents, pour comparer libell\u00e9s de tarifs et cat\u00e9gories CSV
function normaliserLabel(str) {
  return (str || '').trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

// Mots-cl\u00e9s accept\u00e9s dans le libell\u00e9 d'un tarif gratuit, par type d\u00e9tect\u00e9
const MOTS_CLES_GRATUIT = {
  VIP: ['VIP', 'CAMPUS', 'STAFF'],
  ELEVE: ['ELEVE'],
  SUP: ['SUP', 'SUPERVISEUR', 'DEPANNAGE'],
}

function matchSuperviseur(nomCsv, superviseurs) {
  const n = normaliserNom(nomCsv)
  // Correspondance exacte d'abord
  let found = superviseurs.find(s => normaliserNom(`${s.prenom} ${s.nom}`) === n)
  if (found) return found
  // Correspondance sur le prénom seul
  found = superviseurs.find(s => n.includes(normaliserNom(s.prenom)))
  if (found) return found
  // Correspondance sur le nom seul
  found = superviseurs.find(s => n.includes(normaliserNom(s.nom)))
  return found || null
}

export default function ImportCSV({ onClose }) {
  const { profile } = useAuth()
  const [, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [msg, setMsg] = useState(null)
  const [progress, setProgress] = useState(null)

  async function handleFile(e) {
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    setMsg(null)
    setPreview(null)
    setLoading(true)

    const text = await f.text()
    const lines = text.split('\n').filter(l => l.trim())
    const header = lines[0].split(';')

    // Trouver les index des colonnes
    const idx = {
      date: header.findIndex(h => h.trim().toLowerCase().includes('date')),
      category: header.findIndex(h => h.trim().toLowerCase().includes('itemcategory') || h.trim().toLowerCase().includes('category')),
      subtotal: header.findIndex(h => h.trim().toLowerCase().includes('subtotal')),
      accueil: header.findIndex(h => h.trim().toLowerCase().includes('orderclosedby') || h.trim().toLowerCase().includes('closedby')),
      superviseur: header.findIndex(h => h.trim().toLowerCase() === 'superviseur'),
    }

    // Charger les superviseurs depuis la BDD
    const { data: sups } = await supabase.from('profiles')
      .select('id, prenom, nom').eq('role', 'superviseur').eq('actif', true)

    // Charger les tarifs actifs
    const { data: tarifs } = await supabase.from('tarifs')
      .select('id, pole, prix, actif, label').eq('actif', true)

    // Parser les lignes
    const rows = lines.slice(1).map(l => {
      const cols = l.split(';')
      const firstName = normaliserLabel(cols[2])
      const lastName = normaliserLabel(cols[3])
      // Détecter le type de gratuit depuis CustomerLastName (ELEVE, DEPANNAGE) ou CustomerFirstName (STAFF, SUPERVISEUR)
      let gratuitType = null
      if (lastName === 'ELEVE') gratuitType = 'ELEVE'
      else if (lastName === 'CAMPUS' || firstName === 'STAFF') gratuitType = 'VIP'
      else if (lastName === 'DEPANNAGE' || firstName === 'SUPERVISEUR') gratuitType = 'SUP'
      else if (lastName === 'VIP') gratuitType = 'VIP'
      return {
        date: parseDate(cols[idx.date]),
        category: (cols[idx.category] || '').trim().toLowerCase(),
        subtotal: parseMontant(cols[idx.subtotal]),
        accueil: (cols[idx.accueil] || '').trim(),
        superviseur: (cols[idx.superviseur] || '').trim(),
        gratuitType, // 'VIP', 'ELEVE', 'SUP' ou null
      }
    }).filter(r => r.date && (
      CATEGORIES_SPA.includes(r.category) || r.category === CATEGORIE_HYDRO
    ))

    // Grouper par (date, pole) — superviseur peut être différent pour SPA et Hydro
    const parDatePole = {}
    for (const row of rows) {
      const pole = row.category === CATEGORIE_HYDRO ? 'HYDRO' : 'SPA'
      const key = `${row.date}_${pole}`
      if (!parDatePole[key]) {
        parDatePole[key] = {
          date: row.date,
          pole,
          accueilNom: row.accueil,
          superviseurNom: row.superviseur,
          payants: 0, gratuits: 0,
          ca: 0, soins: {},
        }
      }
      const d = parDatePole[key]
      // Garder le premier accueil trouvé pour la date (commun à tous les pôles)
      if (!d.accueilNom && row.accueil) d.accueilNom = row.accueil
      const gratuit = !!row.gratuitType

      if (gratuit) {
        d.gratuits++
        const tarifsGratuits = tarifs?.filter(t => t.pole === pole && t.prix === 0) || []
        const motsCles = MOTS_CLES_GRATUIT[row.gratuitType] || [row.gratuitType || 'VIP']
        let tarifGratuit = tarifsGratuits.find(t =>
          motsCles.some(mot => normaliserLabel(t.label).includes(mot)))
        // Repli : aucun libellé ne correspond mais le pôle a un tarif gratuit -> on le prend
        // pour que le massage soit compté (mieux vaut compté que perdu)
        if (!tarifGratuit) tarifGratuit = tarifsGratuits[0]
        if (tarifGratuit) d.soins[tarifGratuit.id] = (d.soins[tarifGratuit.id] || 0) + 1
      } else {
        d.payants++; d.ca += row.subtotal
        const tarifMatch = tarifs?.filter(t => t.pole === pole && t.prix > 0)
          .sort((a, b) => Math.abs(a.prix - row.subtotal) - Math.abs(b.prix - row.subtotal))[0]
        if (tarifMatch) d.soins[tarifMatch.id] = (d.soins[tarifMatch.id] || 0) + 1
      }
    }

    // Résoudre les superviseurs
    const jours = Object.values(parDatePole).sort((a, b) => a.date.localeCompare(b.date) || a.pole.localeCompare(b.pole))
    const warnings = []

    for (const j of jours) {
      j.superviseurObj = matchSuperviseur(j.superviseurNom, sups)
      j.accueilObj = matchSuperviseur(j.accueilNom, sups)
      if (!j.superviseurObj) warnings.push(`${j.date} : superviseur "${j.superviseurNom}" non trouvé`)
      if (!j.accueilObj && j.accueilNom) warnings.push(`${j.date} : accueil "${j.accueilNom}" non trouvé (ignoré)`)
    }

    setPreview({ jours, warnings, sups, tarifs })
    setLoading(false)
  }

  async function handleImport() {
    if (!preview) return
    setImporting(true)
    setMsg(null)

    const { jours, tarifs } = preview
    let ok = 0, skip = 0, errors = []

    // Le tarif d'accueil dépend de la date (standard ou majoré) : calculé à chaque journée

    // Collecter les dates uniques pour l'accueil (une seule fois par date)
    const accueilParDate = {}

    for (let i = 0; i < jours.length; i++) {
      const j = jours[i]
      setProgress({ current: i + 1, total: jours.length, date: j.date })

      if (!j.superviseurObj) { skip++; continue }

      const ca_ttc = j.ca
      const ca_ht = parseFloat((ca_ttc / TAUX_TVA).toFixed(2))
      const rem_reelle = parseFloat((ca_ht * TAUX_REMUNERATION).toFixed(2))
      const rem_affichee = Math.round(Math.max(rem_reelle, SEUIL_MINIMUM))

      const { data: existing } = await supabase.from('seances')
        .select('id, cloture').eq('date', j.date).eq('pole', j.pole).maybeSingle()

      if (existing?.cloture) { skip++; continue }

      const payload = {
        date: j.date, pole: j.pole,
        superviseur_id: j.superviseurObj.id, superviseur2_id: null,
        cree_par: profile.id,
        total_massage_payant: j.payants,
        total_massage_gratuit: j.gratuits,
        total_massage: j.payants + j.gratuits,
        ca_ttc, ca_ht, remuneration_reelle: rem_reelle, remuneration_affichee: rem_affichee,
        nb_sde_40:0, nb_sde_35:0, nb_sde_33:0, nb_sde_30:0, nb_sde_29:0, nb_sde_20:0,
        nb_vip_gratuit:0, nb_sup:0, nb_eleves:0,
        nb_hydro_60:0, nb_hydro_55:0, nb_hydro_35:0, nb_hydro_33:0, nb_hydro_30:0,
        nb_hydro_29:0, nb_hydro_vip:0,
      }

      let seanceId
      if (existing) {
        await supabase.from('seances').update(payload).eq('id', existing.id)
        await supabase.from('seance_soins').delete().eq('seance_id', existing.id)
        seanceId = existing.id
      } else {
        const { data, error } = await supabase.from('seances').insert(payload).select().single()
        if (error) { errors.push(`${j.date} ${j.pole} : ${error.message}`); continue }
        seanceId = data.id
      }

      const soinsPayload = Object.entries(j.soins).map(([tarif_id, quantite]) => {
        const t = tarifs.find(x => x.id === tarif_id)
        return { seance_id: seanceId, tarif_id, label: t?.label || `Prix ${t?.prix}€`, prix: t?.prix || 0, quantite, total: quantite * (t?.prix || 0), gratuit: (t?.prix || 0) === 0 }
      })
      if (soinsPayload.length > 0) await supabase.from('seance_soins').insert(soinsPayload)
      ok++

      // Accueil : une seule fois par date
      if (j.accueilObj && !accueilParDate[j.date]) {
        accueilParDate[j.date] = true
        const { data: exA } = await supabase.from('seances_accueil')
          .select('id, cloture').eq('date', j.date).eq('superviseur_id', j.accueilObj.id).maybeSingle()
        if (!exA && !exA?.cloture) {
          const { montant: montantAccueil } = await tarifAccueilPour(j.date)
          if (montantAccueil === null) {
            errors.push(`${j.date} accueil : aucun tarif trouvé (Soins & Tarifs, onglet Journées)`)
          } else {
            await supabase.from('seances_accueil').insert({
              date: j.date, superviseur_id: j.accueilObj.id,
              montant: montantAccueil, cree_par: profile.id
            })
          }
        }
      }
    }

    setProgress(null)
    setImporting(false)
    setMsg({
      type: errors.length > 0 ? 'warning' : 'success',
      text: `Import terminé : ${ok} séance${ok > 1 ? 's' : ''} importée${ok > 1 ? 's' : ''}${skip > 0 ? `, ${skip} ignorée${skip > 1 ? 's' : ''}` : ''}${errors.length > 0 ? `. Erreurs : ${errors.join(', ')}` : ''}.`
    })
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{maxWidth:700, maxHeight:'85vh', overflowY:'auto'}}>
        <div className="modal-header">
          <div>
            <h3>Import depuis Spa Booker</h3>
            <p className="modal-sub">Fichier CSV des commandes</p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

        {!preview && (
          <div>
            <p style={{fontSize:13, color:'var(--stone)', marginBottom:16, lineHeight:1.6}}>
              Exportez le rapport "Orders Report" depuis Spa Booker et importez-le ici.
              L'import créera automatiquement les séances SPA, Hydro et Accueil pour chaque jour.
            </p>
            <div className="form-group">
              <label>Fichier CSV</label>
              <input type="file" accept=".csv" onChange={handleFile}
                style={{padding:'8px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13}} />
            </div>
            {loading && <div className="spinner" />}
          </div>
        )}

        {preview && !msg && (
          <div>
            <div className="alert alert-info" style={{marginBottom:16}}>
              {preview.jours.length} jour{preview.jours.length > 1 ? 's' : ''} trouvé{preview.jours.length > 1 ? 's' : ''} dans le fichier.
            </div>

            {preview.warnings.length > 0 && (
              <div className="alert alert-warning" style={{marginBottom:16}}>
                <strong>Avertissements :</strong>
                <ul style={{margin:'8px 0 0', paddingLeft:20}}>
                  {preview.warnings.map((w, i) => <li key={i} style={{fontSize:12}}>{w}</li>)}
                </ul>
              </div>
            )}

            <div style={{maxHeight:300, overflowY:'auto', marginBottom:16}}>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Pôle</th>
                    <th className="num">Payants</th>
                    <th className="num">Gratuits</th>
                    <th className="num">CA TTC</th>
                    <th>Superviseur</th>
                    <th>Accueil</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.jours.map(j => (
                    <tr key={`${j.date}_${j.pole}`} style={!j.superviseurObj ? {background:'#FFF3F3'} : {}}>
                      <td>{new Date(j.date).toLocaleDateString('fr-FR')}</td>
                      <td><span className={j.pole === 'HYDRO' ? 'badge badge-hydro' : 'badge badge-spa'}>{j.pole}</span></td>
                      <td className="num">{j.payants}</td>
                      <td className="num">{j.gratuits}</td>
                      <td className="num">{j.ca.toFixed(2)} €</td>
                      <td>
                        {j.superviseurObj
                          ? <span style={{color:'var(--success)'}}>{j.superviseurObj.prenom} {j.superviseurObj.nom}</span>
                          : <span style={{color:'var(--error)'}}>{j.superviseurNom} ⚠</span>}
                      </td>
                      <td>
                        {j.accueilObj
                          ? <span style={{color:'var(--success)', fontSize:12}}>{j.accueilObj.prenom}</span>
                          : <span style={{color:'var(--stone)', fontSize:12}}>{j.accueilNom || '—'}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {progress && (
              <div className="alert alert-info">
                Import en cours… {progress.current}/{progress.total} — {new Date(progress.date).toLocaleDateString('fr-FR')}
              </div>
            )}

            <div style={{display:'flex', gap:10, justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={() => { setPreview(null); setFile(null) }}>
                Changer de fichier
              </button>
              <button className="btn btn-bordeaux" onClick={handleImport} disabled={importing}>
                {importing ? 'Import en cours...' : `Importer ${preview.jours.length} jour${preview.jours.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {msg && (
          <div style={{display:'flex', justifyContent:'flex-end', marginTop:16}}>
            <button className="btn btn-primary" onClick={onClose}>Fermer</button>
          </div>
        )}
      </div>
    </div>
  )
}
